import { useState } from 'react';
import { Panel, CountKpi } from '../components/CommandCentreParts.js';
import { EmptyState } from '../components/NoData.js';
import { useApi, type GetToken } from '../lib/useApi.js';
import { gbp, count, faintIfNull, NO_DATA } from '../lib/format.js';

interface Place {
  place: string; canonicalName: string | null; targetType: string | null;
  campaign: string; isNamedTown: boolean; milesFromDoncaster: number | null;
  impressions: number; clicks: number; costGbp: number; conversions: number;
}
interface CampaignSplit {
  campaign: string; namedTownCostGbp: number; elsewhereCostGbp: number;
  namedTownClicks: number; elsewhereClicks: number; places: number;
}
interface GeographyResponse {
  window: { from: string; to: string; days: number };
  locationType: string;
  locationTypeMeaning: string;
  totals: { placesServed: number; costGbp: number | null; clicks: number };
  byCampaign: CampaignSplit[];
  places: Place[];
  townsNotYetServed: { name: string; tier: number; approx_miles_from_doncaster: number | null }[];
  caveat: string;
  generatedUtc: string;
}

export function Geography({ getToken }: { getToken: GetToken }): JSX.Element {
  const [presence, setPresence] = useState(true);
  const path = `/api/geography?days=30&locationType=${presence ? 'LOCATION_OF_PRESENCE' : 'AREA_OF_INTEREST'}`;
  const { data, error } = useApi<GeographyResponse>(path, getToken);
  const [campaign, setCampaign] = useState('__all__');

  if (error) return <EmptyState title="Could not load geography" tag="Error">{error}</EmptyState>;
  if (!data) return <EmptyState title="Loading…" tag="Please wait">Reading geographic performance.</EmptyState>;

  /* An empty INTEREST view does not mean nothing ran. Google only reports an
     area of interest when it differs from where the person was, so at low
     volume there may be none while presence data exists. Saying "nothing
     served" there would be false. */
  if (data.totals.placesServed === 0) {
    return presence ? (
      <EmptyState title="No geographic data yet" tag="No data">
        Nothing has served in the window {data.window.from} to {data.window.to}.
      </EmptyState>
    ) : (
      <EmptyState title="Nothing searching about a named place" tag="No data">
        Google only reports an area of interest when it differs from where the
        person was. Adverts did serve in this window — switch to
        &ldquo;Where they were&rdquo; to see it.
      </EmptyState>
    );
  }

  const campaigns = Array.from(new Set(data.places.map((p) => p.campaign))).sort();
  const rows = data.places
    .filter((p) => p.costGbp > 0)
    .filter((p) => campaign === '__all__' || p.campaign === campaign);

  return (
    <div className="stack">
      <Panel
        title="Where the money went"
        subtitle={`${data.locationTypeMeaning} Window ${data.window.from} to ${data.window.to}.`}
      >
        <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
          <button
            className="btn-ghost"
            style={presence ? { borderColor: 'var(--accent)', color: 'var(--accent)' } : undefined}
            onClick={() => setPresence(true)}
          >
            Where they were
          </button>
          <button
            className="btn-ghost"
            style={!presence ? { borderColor: 'var(--accent)', color: 'var(--accent)' } : undefined}
            onClick={() => setPresence(false)}
          >
            Where they searched about
          </button>
          <select className="select" value={campaign} onChange={(e) => setCampaign(e.target.value)}>
            <option value="__all__">All campaigns</option>
            {campaigns.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>

        <div className="kpi-grid">
          <CountKpi label="Places served" value={data.totals.placesServed} />
          <CountKpi label="Spend" value={data.totals.costGbp} money />
          <CountKpi label="Clicks" value={data.totals.clicks || null} />
        </div>
      </Panel>

      <Panel
        title="By campaign"
        subtitle="Cyber Essentials and CE Plus are deliberately UK-wide, so spend outside the named towns is expected there. Core MSP targets a 50-mile radius, so it is the one worth watching."
      >
        <table className="data">
          <thead>
            <tr>
              <th>Campaign</th>
              <th className="num">Named towns</th>
              <th className="num">Elsewhere</th>
              <th className="num">Places</th>
            </tr>
          </thead>
          <tbody>
            {data.byCampaign.map((c) => (
              <tr key={c.campaign}>
                <td>{c.campaign}</td>
                <td className="num" style={{ color: 'var(--positive)' }}>
                  {gbp(c.namedTownCostGbp)}
                  <div style={{ fontSize: 11, color: 'var(--text-faint)' }}>{count(c.namedTownClicks)} clicks</div>
                </td>
                <td className="num">
                  {gbp(c.elsewhereCostGbp)}
                  <div style={{ fontSize: 11, color: 'var(--text-faint)' }}>{count(c.elsewhereClicks)} clicks</div>
                </td>
                <td className="num">{count(c.places)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Panel>

      <Panel title={`Places (${rows.length})`} subtitle="Ordered by cost. Only places that have cost money are listed.">
        <table className="data">
          <thead>
            <tr>
              <th>Place</th>
              <th>Campaign</th>
              <th></th>
              <th className="num">Impr.</th>
              <th className="num">Clicks</th>
              <th className="num">Cost</th>
              <th className="num">Conv.</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((p, i) => (
              <tr key={`${p.place}|${p.campaign}|${i}`}>
                <td>
                  {p.place}
                  {p.canonicalName ? (
                    <div style={{ fontSize: 11, color: 'var(--text-faint)' }}>{p.canonicalName}</div>
                  ) : null}
                </td>
                <td style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                  {p.campaign.replace('GE — ', '').replace(' — Search', '')}
                </td>
                <td>
                  {p.isNamedTown ? (
                    <span className="pill good" title={p.milesFromDoncaster !== null ? `${p.milesFromDoncaster} miles from Doncaster` : undefined}>
                      Named town
                    </span>
                  ) : (
                    <span className="pill">Elsewhere</span>
                  )}
                </td>
                <td className="num" style={faintIfNull(p.impressions)}>{count(p.impressions)}</td>
                <td className="num">{count(p.clicks)}</td>
                <td className="num" style={{ fontWeight: 500 }}>{gbp(p.costGbp)}</td>
                <td className="num" style={{ color: p.conversions === 0 ? 'var(--text-faint)' : 'var(--positive)' }}>
                  {count(p.conversions)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Panel>

      {data.townsNotYetServed.length > 0 ? (
        <Panel
          title="Towns we track that have not been served"
          subtitle="Never shown an advert, so there is nothing to judge — different from a town that served and produced nothing."
        >
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {data.townsNotYetServed.map((t) => (
              <span key={t.name} className="pill">
                {t.name}{t.approx_miles_from_doncaster !== null ? ` · ${t.approx_miles_from_doncaster} mi` : ''}
              </span>
            ))}
          </div>
        </Panel>
      ) : null}

      <div className="panel">
        <p style={{ fontSize: 12, color: 'var(--text-faint)', margin: 0 }}>
          {data.caveat} {NO_DATA} appears where a figure has not been measured rather than measured as zero.
        </p>
      </div>
    </div>
  );
}
